import { Link, useParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import NewsCard from "@/components/feature/NewsCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";
import { getNewsById, getRelatedNews } from "@/utils/content";

export default function NewsDetailPage() {
  const { id } = useParams();
  const news = getNewsById(id);

  useDocumentMeta(
    news ? `动漫天堂 - ${news.title}` : "动漫天堂 - 资讯未找到",
    news ? news.excerpt : "该资讯不存在或已下线，请返回动漫资讯列表查看更多内容。",
    news ? `${news.title},${news.tags.join(",")},动漫资讯,动漫天堂` : "动漫天堂,动漫资讯",
    `/news/detail/${id || ""}`
  );

  if (!news) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteNavbar />
        <main className="flex flex-1 items-center justify-center px-4 pt-20">
          <div className="mx-auto max-w-md rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
              <i className="ri-article-line" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">没有找到这篇资讯</h1>
            <p className="mt-2 text-sm leading-6 text-foreground-600">
              链接可能已经失效，你可以回到资讯列表查看最新内容。
            </p>
            <Link
              to="/news"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedNews(news, 3);
  const fallback = newsList.filter((item) => item.id !== news.id).slice(0, 3);
  const relatedList = related.length ? related : fallback;
  const moreNews = newsList.filter((item) => item.id !== news.id).slice(0, 4);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteNavbar />

      <main className="flex-1 pt-16 md:pt-20">
        <div className="mx-auto w-full max-w-page px-4 pt-6 md:px-6 md:pt-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
            <Link to="/news" className="transition-colors hover:text-primary-600">动漫资讯</Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
            <span className="line-clamp-1 text-foreground-800">{news.title}</span>
          </nav>
        </div>

        <div className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
            <article className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold text-background-50">
                  {news.category}
                </span>
                {news.tags.slice(0, 3).map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-300 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-4xl md:leading-tight">
                {news.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  发布时间：{news.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" />
                  {news.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line" />
                  演示阅读量：{1200 + news.id * 137}
                </span>
              </div>

              <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-100">
                <div className="relative h-[220px] w-full md:h-[380px]">
                  <img
                    src={news.cover}
                    alt={`${news.title} 资讯配图`}
                    title={`${news.title} 动漫资讯`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <p className="mt-6 border-l-2 border-primary-500 bg-background-100/60 px-4 py-3 text-sm leading-7 text-foreground-700">
                {news.excerpt}
              </p>

              <div className="mt-6 space-y-5">
                {news.content.map((paragraph, index) => (
                  <p key={index} className="text-[15px] leading-8 text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-100/50 px-5 py-4">
                <p className="text-xs leading-6 text-foreground-500">
                  本文为动漫天堂演示内容，用于展示资讯页面的排版与关联推荐结构，不代表真实新闻事件。
                </p>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-arrow-left-line" />
                  返回资讯列表
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  <i className="ri-compass-3-line" />
                  浏览动漫分类
                </Link>
              </div>

              <section className="mt-10">
                <div className="flex items-center gap-2">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  <h2 className="font-heading text-lg font-bold text-foreground-950">相关推荐资讯</h2>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {relatedList.map((item) => (
                    <NewsCard key={item.id} news={item} />
                  ))}
                </div>
              </section>
            </article>

            <aside className="w-full">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-newspaper-line text-base text-accent-600" />
                  更多资讯
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {moreNews.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/detail/${item.id}`}
                      className="group border-b border-background-200 pb-4 last:border-b-0 last:pb-0"
                    >
                      <span className="text-[11px] text-foreground-400">{item.date} · {item.category}</span>
                      <span className="mt-1 line-clamp-2 block text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-primary-50/60 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">找作品资料？</h2>
                <p className="mt-2 text-xs leading-6 text-foreground-600">
                  在分类页按类型、地区、年份筛选，能更快找到你想了解的动漫。
                </p>
                <Link
                  to="/category"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  进入动漫分类
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}